// src/features/feeds/components/FeedSourceItem.tsx

import { Group, Text, ActionIcon, Box } from '@mantine/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical, IconEdit, IconTrash } from '@tabler/icons-react';
import type { FeedSourceData } from '@/lib/types/Feed';

interface FeedSourceItemProps {
  source: FeedSourceData;
  index: number;
  playlistLabel: string | null;
  onEdit: (source: FeedSourceData) => void;
  onDelete: (source: FeedSourceData) => void;
}

export function FeedSourceItem({ source, index, playlistLabel, onEdit, onDelete }: FeedSourceItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: source.feedSourceId || `new-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      py="xs"
      px="sm"
      bg={isDragging ? 'gray.0' : undefined}
      bd="1px solid var(--mantine-color-gray-2)"
      style={{
        ...style,
        borderRadius: 'var(--mantine-radius-sm)',
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          {/* Drag handle */}
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            style={{ cursor: 'grab', touchAction: 'none' }}
            {...attributes}
            {...listeners}
          >
            <IconGripVertical size={16} />
          </ActionIcon>

          {/* Position number */}
          <Text size="sm" c="dimmed" fw={600} w={24} ta="center">
            #{index + 1}
          </Text>

          {/* Source title */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>
              {source.title}
            </Text>
            {playlistLabel && (
              <Text size="xs" c="dimmed" truncate>
                {playlistLabel}
              </Text>
            )}
          </Box>
        </Group>

        <Group gap={4}>
          <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => onEdit(source)}>
            <IconEdit size={14} />
          </ActionIcon>
          <ActionIcon variant="subtle" color="red" size="sm" onClick={() => onDelete(source)}>
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      </Group>
    </Box>
  );
}
